import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppContext } from '../../../App'
import { ChartEvents } from '../chart.events'
import { UserAlert } from '../../../proto/generated/nexus'
import { createAlert, removeAlert, restartAlert } from '../../../services/apis/alerts'
import {
    createSnapshotFromUserAlert,
    getAlertMessageSymbolDisplayName,
    getCreateUserAlertRequestObject,
    getUserAlertUnderlyingSecurity,
    userAlertWithUpdatedRhsFixedPrice
} from '../../../util/alerts'
import { removeUserAlert, setAlertModalData, updateUserAlert } from '../../../reducer/alerts.reducer'
import commonActions from '../../../actions/common.actions'
import { selectIndicators } from '../../../selectors/chart'
import { selectIsEmbedded } from '../../../selectors/device'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { SecurityCategory_CategoryCode } from '../../../proto/generated/security'
import { callNative } from '../../../util/nativeBridge'
import ReusableDialog from '../../common/reusableDialog'

type Props = {
    chartId: string
}

const ChartAlertActions = ({ chartId }: Props) => {
    const dispatch = useDispatch()
    const isEmbedded = useSelector(selectIsEmbedded)
    const indicators = useSelector(selectIndicators)
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(chartId)
    const inFlightPriceCommitRef = useRef<Set<string>>(new Set())
    const recentPriceCommitRef = useRef<Map<string, number>>(new Map())
    const [pendingDeleteAlert, setPendingDeleteAlert] = useState<UserAlert | null>(null)

    const deleteDialogContent = useMemo(() => {
        if (!pendingDeleteAlert) return ''
        return `Do you really want to delete your alert '${pendingDeleteAlert.name ? pendingDeleteAlert.name : pendingDeleteAlert.message ? pendingDeleteAlert.message : ''}'?`.trim()
    }, [pendingDeleteAlert])

    const onPriceCommit = useCallback(
        async (event: CustomEvent<{ alert: UserAlert; newPrice: number }>) => {
            const detail = event.detail
            if (!detail?.alert?.id) return
            const { alert, newPrice } = detail
            const dedupeKey = `${alert.id}:${newPrice}`
            const now = Date.now()
            const lastSeen = recentPriceCommitRef.current.get(dedupeKey) ?? 0
            if (inFlightPriceCommitRef.current.has(dedupeKey) || now - lastSeen < 1200) {
                return
            }
            inFlightPriceCommitRef.current.add(dedupeKey)
            try {
                const updated = await userAlertWithUpdatedRhsFixedPrice(alert, newPrice, {
                    regenerateMessage: true,
                    indicators,
                    symbolDisplayName: getAlertMessageSymbolDisplayName(alert),
                    chartTimeBucket: alert.timeBucket,
                })
                const result = await createAlert(getCreateUserAlertRequestObject(updated))
                dispatch(updateUserAlert(result))
            } catch (e: any) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: e?.message || 'Failed to update alert price',
                    errorType: 'error'
                })
            } finally {
                inFlightPriceCommitRef.current.delete(dedupeKey)
                recentPriceCommitRef.current.set(dedupeKey, Date.now())
            }
        },
        [dispatch, indicators]
    )

    const onDelete = useCallback((event: CustomEvent<{ alert: UserAlert }>) => {
        const alert = event.detail?.alert
        if (!alert?.id) return
        setPendingDeleteAlert(alert)
    }, [])

    const onDeleteDialogClose = useCallback(() => {
        setPendingDeleteAlert(null)
    }, [])

    const onDeleteDialogSubmit = useCallback(async () => {
        const alert = pendingDeleteAlert
        if (!alert?.id) return
        setPendingDeleteAlert(null)
        try {
            await removeAlert({ id: alert.id })
            dispatch(removeUserAlert(alert.id))
        } catch (e: any) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: e?.message || 'Failed to remove alert',
                errorType: 'error'
            })
        }
    }, [dispatch, pendingDeleteAlert])

    const onRestart = useCallback(
        async (event: CustomEvent<{ alert: UserAlert }>) => {
            const alert = event.detail?.alert
            if (!alert?.id) return
            try {
                const [underlyingSecurities] = getUserAlertUnderlyingSecurity(alert)
                const security = underlyingSecurities[0]
                if (security) {
                    const actualSec = await getSecurityAsync(getSecurityKey(security.exchange, security.code))
                    if (actualSec) {
                        const isOptionOrFuture =
                            actualSec.categoryCode === SecurityCategory_CategoryCode.OPTIONS ||
                            actualSec.categoryCode === SecurityCategory_CategoryCode.FUTURES
                        if (isOptionOrFuture && actualSec.expiry && actualSec.expiry.expiry < Date.now() / 1000) {
                            commonActions.setApiError(dispatch, {
                                error: true,
                                message: 'Security is expired',
                                errorType: 'error'
                            })
                            return
                        }
                    }
                }
                if (alert.expiry < Date.now()) {
                    const chartSnapshot = createSnapshotFromUserAlert(alert, indicators)
                    dispatch(
                        setAlertModalData({
                            chartSnapshot,
                            edit: true,
                            alert
                        })
                    )
                    return
                }
                const updatedAlert = await restartAlert({ id: alert.id })
                dispatch(updateUserAlert(updatedAlert))
            } catch (e: any) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: e?.message || 'Failed to restart alert',
                    errorType: 'error'
                })
            }
        },
        [dispatch, indicators]
    )

    const onEdit = useCallback(
        async (event: CustomEvent<{ alert: UserAlert }>) => {
            const alert = event.detail?.alert
            if (!alert) return
            if (isEmbedded) {
                callNative('onAlertDoubleClicked', alert.id)
                return
            }
            try {
                const [underlyingSecurities] = getUserAlertUnderlyingSecurity(alert)
                const security = underlyingSecurities[0]
                if (security) {
                    const actualSec = await getSecurityAsync(getSecurityKey(security.exchange, security.code))
                    if (actualSec) {
                        const isOptionOrFuture =
                            actualSec.categoryCode === SecurityCategory_CategoryCode.OPTIONS ||
                            actualSec.categoryCode === SecurityCategory_CategoryCode.FUTURES
                        if (isOptionOrFuture && actualSec.expiry && actualSec.expiry.expiry < Date.now() / 1000) {
                            commonActions.setApiError(dispatch, {
                                error: true,
                                message: 'Security is expired',
                                errorType: 'error'
                            })
                            return
                        }
                    }
                }
                const chartSnapshot = createSnapshotFromUserAlert(alert, indicators)
                dispatch(
                    setAlertModalData({
                        chartSnapshot,
                        edit: true,
                        alert
                    })
                )
            } catch {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: 'Failed to open alert editor',
                    errorType: 'error'
                })
            }
        },
        [dispatch, indicators, isEmbedded]
    )

    useEffect(() => {
        if (!chart) return
        chart.subscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_PRICE_COMMIT, onPriceCommit as EventListener)
        chart.subscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_DELETE, onDelete as EventListener)
        chart.subscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_RESTART, onRestart as EventListener)
        chart.subscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_EDIT, onEdit as EventListener)
        return () => {
            chart.unsubscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_PRICE_COMMIT, onPriceCommit as EventListener)
            chart.unsubscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_DELETE, onDelete as EventListener)
            chart.unsubscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_RESTART, onRestart as EventListener)
            chart.unsubscribeEvent(ChartEvents.CHART_ALERT_ON_CHART_EDIT, onEdit as EventListener)
        }
    }, [chart, onDelete, onEdit, onPriceCommit, onRestart])

    return (
        <ReusableDialog
            key={pendingDeleteAlert ? `delete-alert-dialog-${pendingDeleteAlert.id}` : 'delete-alert-dialog-closed'}
            open={!!pendingDeleteAlert}
            onClose={onDeleteDialogClose}
            onSubmit={onDeleteDialogSubmit}
            title={'Delete alert'}
            content={deleteDialogContent}
            submitText={'Delete'}
            submitButtonProps={{
                color: 'error',
            }}
            dialogContentSx={{ pt: 0, px: 2.5, pb: 1 }}
            dialogActionsSx={{ px: 2.5, pb: 2, pt: 1 }}
            cancelVariant={'outlined'}
            cancelText={'No'}
        />
    )
}

export default ChartAlertActions
